import { passkeyClient } from "@better-auth/passkey/client";
import { getBaseUrl } from "@repo/utils";
import {
	adminClient,
	emailOTPClient,
	inferAdditionalFields,
	magicLinkClient,
	organizationClient,
	twoFactorClient,
} from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import type { auth } from ".";
import { ac, admin, member, owner } from "./permissions";

/**
 * Create the auth client with all plugins.
 *
 * Type inference comes directly from better-auth's plugin system:
 * - inferAdditionalFields<typeof auth>() provides type inference for additional
 *   user fields defined in auth.ts (e.g., onboardingComplete, locale)
 * - organizationClient, adminClient, etc. provide their own type inference
 *
 * We export the client directly without manual type overrides to ensure
 * types match better-auth's actual API.
 *
 * `baseURL` is required so the client works in SSR contexts where there is no
 * `window.location` to resolve relative paths against. In the browser the URL
 * still points at the same origin, so behavior is unchanged.
 */
export const authClient = createAuthClient({
	baseURL: getBaseUrl(),
	plugins: [
		inferAdditionalFields<typeof auth>(),
		emailOTPClient(),
		magicLinkClient(),
		organizationClient({
			ac,
			roles: {
				owner,
				admin,
				member,
			},
			dynamicAccessControl: {
				enabled: true,
			},
		}),
		adminClient(),
		passkeyClient(),
		twoFactorClient(),
	],
});

export type AuthClientErrorCodes = typeof authClient.$ERROR_CODES & {
	INVALID_INVITATION: string;
};
